import { IAction, KEEP_THE_SAME, StateStatus } from 'redux/utils/common';
import * as Types from './types';
import { IDataItems } from 'services/api/category';
import { PropertyFilterType } from '../configuration';
import { IMiniProfile } from 'services/api/product';
import { IDataItems as IDataItemsSpec } from 'services/api/specificationattribute';
import { IMunicipality } from 'services/api/municipality';
import { ICity } from 'services/api/city';
import * as ProductService from 'services/api/product';
import { IDataItems as IDataItemsZone } from 'services/api/zones';
import { PriceType } from 'constants/priceType';

export interface IFiltersSelection {
  fetch: boolean;
  [PropertyFilterType.AdminFilters]: {
    isChecked: boolean;
    brief?: string;
    data: {
      published: number | null;
      availability_status: Array<number>;
      agents: Array<number>;
      owners: Array<number>;
      refered_by: Array<number>;
      representative: Array<number>;
      dateAdded: Date | null;
      updated_from: number | null;
      registrationdepartment: string | null;
      registrationnumber: string | null;
      sheet: string | null;
      plan: string | null;
      block: string | null;
      parcelnumber: string | null;
    };
  };
  [PropertyFilterType.PropertyID]: {
    isChecked: boolean;
    brief?: string;
    data: {
      ids: number[] | null;
    };
  };
  [PropertyFilterType.PropertyType]: {
    isChecked: boolean;
    brief?: string;
    data: {
      offerType: number | null;
      propertyType: number | null;
      propertySubType: Array<number>;
    };
  };
  [PropertyFilterType.Price]: {
    isChecked: boolean;
    brief?: string;
    data: {
      minPrice: number | null;
      maxPrice: number | null;
      priceType: PriceType | null;
    };
  };
  [PropertyFilterType.Bedrooms]: {
    isChecked: boolean;
    brief?: string;
    data: {
      bedrooms: Array<number>;
    };
  };
  [PropertyFilterType.InternalArea]: {
    isChecked: boolean;
    brief?: string;
    data: {
      internal_covered_areas: string | null;
    };
  };
  [PropertyFilterType.Condition]: {
    isChecked: boolean;
    brief?: string;
    data: {
      condition: string | number | null;
    };
  };
  [PropertyFilterType.YearBuilt]: {
    isChecked: boolean;
    brief?: string;
    data: {
      year_built: string | null;
    };
  };
  [PropertyFilterType.PlotSize]: {
    isChecked: boolean;
    brief?: string;
    data: {
      plot_land_size: string | null;
    };
  };
  [PropertyFilterType.Zone]: {
    isChecked: boolean;
    brief?: string;
    data: {
      zone: Array<{ id: string; label: string }>;
    };
  };
  [PropertyFilterType.BuildingDensity]: {
    isChecked: boolean;
    brief?: string;
    data: {
      building_density: string | null;
    };
  };
  [PropertyFilterType.MaxFloors]: {
    isChecked: boolean;
    brief?: string;
    data: {
      max_floors: string | null;
    };
  };
  [PropertyFilterType.Frontage]: {
    isChecked: boolean;
    brief?: string;
    data: {
      frontage: string | null;
    };
  };
  [PropertyFilterType.LandOptions]: {
    isChecked: boolean;
    brief?: string;
    data: {
      electricity: boolean | null;
      water_supply: boolean | null;
      road_access: boolean | null;
    };
  };
  [PropertyFilterType.ParkingSpaces]: {
    isChecked: boolean;
    brief?: string;
    data: {
      parking_spots: string | null;
    };
  };
  [PropertyFilterType.ReturnOnInvestment]: {
    isChecked: boolean;
    brief?: string;
    data: {
      roi: string | null;
    };
  };
  [PropertyFilterType.HeatingAndCooling]: {
    isChecked: boolean;
    brief?: string;
    data: {
      air_conditioning: Array<number>;
      heating: Array<number>;
      heating_type: Array<number>;
    };
  };
  [PropertyFilterType.Furnishing]: {
    isChecked: boolean;
    brief?: string;
    data: {
      furnishing: Array<number>;
    };
  };
  [PropertyFilterType.Floor]: {
    isChecked: boolean;
    brief?: string;
    data: {
      floor: string | null;
    };
  };
  [PropertyFilterType.Bathrooms]: {
    isChecked: boolean;
    brief?: string;
    data: {
      bathrooms: Array<number>;
    };
  };
  [PropertyFilterType.WC]: {
    isChecked: boolean;
    brief?: string;
    data: {
      wcs: Array<number>;
    };
  };
  [PropertyFilterType.SuitableFor]: {
    isChecked: boolean;
    brief?: string;
    data: {
      suitable_for: string | null;
    };
  };
  [PropertyFilterType.Basement]: {
    isChecked: boolean;
    brief?: string;
    data: {
      basement: boolean | null;
    };
  };
  [PropertyFilterType.Attic]: {
    isChecked: boolean;
    brief?: string;
    data: {
      attic: boolean | null;
    };
  };
  [PropertyFilterType.TitleDeed]: {
    isChecked: boolean;
    brief?: string;
    data: {
      title_deed: boolean | null;
    };
  };
  [PropertyFilterType.VAT]: {
    isChecked: boolean;
    brief?: string;
    data: {
      vat_options: string | null;
    };
  };
  [PropertyFilterType.EnergyEfficiency]: {
    isChecked: boolean;
    brief?: string;
    data: {
      energy_certificate: string | number | null;
    };
  };
  [PropertyFilterType.Location]: {
    isChecked: boolean;
    brief?: string;
    data: {
      location: {
        city: Array<number>;
        municipality: Array<number>;
      };
    };
  };
  [PropertyFilterType.Other]: {
    isChecked: boolean;
    brief?: string;
    data: {
      [key: string]: string | undefined;
    };
  };
}

export const FiltersSelectionInitialValue: IFiltersSelection = {
  fetch: true,
  [PropertyFilterType.AdminFilters]: {
    isChecked: false,
    data: {
      published: null,
      availability_status: [],
      agents: [],
      owners: [],
      refered_by: [],
      representative: [],
      dateAdded: null,
      updated_from: null,
      registrationdepartment: null,
      registrationnumber: null,
      sheet: null,
      plan: null,
      block: null,
      parcelnumber: null,
    },
  },
  [PropertyFilterType.PropertyID]: {
    isChecked: false,
    data: {
      ids: null,
    },
  },
  [PropertyFilterType.PropertyType]: {
    isChecked: false,
    data: {
      offerType: null,
      propertyType: null,
      propertySubType: [],
    },
  },
  [PropertyFilterType.Price]: {
    isChecked: false,
    data: {
      minPrice: null,
      maxPrice: null,
      priceType: null,
    },
  },
  [PropertyFilterType.Bedrooms]: {
    isChecked: false,
    data: {
      bedrooms: [],
    },
  },
  [PropertyFilterType.InternalArea]: {
    isChecked: false,
    data: {
      internal_covered_areas: null,
    },
  },
  [PropertyFilterType.Condition]: {
    isChecked: false,
    data: {
      condition: null,
    },
  },
  [PropertyFilterType.YearBuilt]: {
    isChecked: false,
    data: {
      year_built: null,
    },
  },
  [PropertyFilterType.PlotSize]: {
    isChecked: false,
    data: {
      plot_land_size: null,
    },
  },
  [PropertyFilterType.Zone]: {
    isChecked: false,
    data: {
      zone: [],
    },
  },
  [PropertyFilterType.BuildingDensity]: {
    isChecked: false,
    data: {
      building_density: null,
    },
  },
  [PropertyFilterType.MaxFloors]: {
    isChecked: false,
    data: {
      max_floors: null,
    },
  },
  [PropertyFilterType.Frontage]: {
    isChecked: false,
    data: {
      frontage: null,
    },
  },
  [PropertyFilterType.LandOptions]: {
    isChecked: false,
    data: {
      electricity: null,
      water_supply: null,
      road_access: null,
    },
  },
  [PropertyFilterType.ParkingSpaces]: {
    isChecked: false,
    data: {
      parking_spots: null,
    },
  },
  [PropertyFilterType.ReturnOnInvestment]: {
    isChecked: false,
    data: {
      roi: null,
    },
  },
  [PropertyFilterType.HeatingAndCooling]: {
    isChecked: false,
    data: {
      air_conditioning: [],
      heating: [],
      heating_type: [],
    },
  },
  [PropertyFilterType.Furnishing]: {
    isChecked: false,
    data: {
      furnishing: [],
    },
  },
  [PropertyFilterType.Floor]: {
    isChecked: false,
    data: {
      floor: null,
    },
  },
  [PropertyFilterType.Bathrooms]: {
    isChecked: false,
    data: {
      bathrooms: [],
    },
  },
  [PropertyFilterType.WC]: {
    isChecked: false,
    data: {
      wcs: [],
    },
  },
  [PropertyFilterType.SuitableFor]: {
    isChecked: false,
    data: {
      suitable_for: null,
    },
  },
  [PropertyFilterType.Basement]: {
    isChecked: false,
    data: {
      basement: null,
    },
  },
  [PropertyFilterType.Attic]: {
    isChecked: false,
    data: {
      attic: null,
    },
  },
  [PropertyFilterType.TitleDeed]: {
    isChecked: false,
    data: {
      title_deed: null,
    },
  },
  [PropertyFilterType.VAT]: {
    isChecked: false,
    data: {
      vat_options: null,
    },
  },
  [PropertyFilterType.EnergyEfficiency]: {
    isChecked: false,
    data: {
      energy_certificate: null,
    },
  },
  [PropertyFilterType.Location]: {
    isChecked: false,
    data: {
      location: {
        city: [],
        municipality: [],
      },
    },
  },
  [PropertyFilterType.Other]: {
    isChecked: false,
    data: {},
  },
};

export interface IState {
  category: {
    status: StateStatus | null;
    data: Array<IDataItems>;
  };
  agents: {
    status: StateStatus | null;
    data: Array<IMiniProfile>;
  };
  customAttributes: {
    status: StateStatus | null;
    data: Array<IDataItemsSpec>;
  };
  city: {
    data: Array<ICity>;
    status: StateStatus | null;
  };
  municipality: {
    data: Array<IMunicipality>;
    status: StateStatus | null;
  };
  filtersSelection: IFiltersSelection;
  segments: {
    data: ProductService.ISegments | null;
    status: StateStatus | null;
  };
  clientCustomers: {
    data: Array<ProductService.IMiniProfile>;
    status: StateStatus | null;
  };
  zoneList: {
    status: StateStatus | null;
    data: IDataItemsZone[] | null;
  };
}

const initialState: IState = {
  category: {
    status: null,
    data: [],
  },
  agents: {
    status: null,
    data: [],
  },
  customAttributes: {
    status: null,
    data: [],
  },
  city: {
    data: [],
    status: null,
  },
  municipality: {
    data: [],
    status: null,
  },
  filtersSelection: FiltersSelectionInitialValue,
  segments: {
    data: null,
    status: null,
  },
  clientCustomers: {
    data: [],
    status: null,
  },
  zoneList: {
    status: null,
    data: null,
  },
};

const ProductFilterReducer = (
  state: IState = initialState,
  action: IAction
): IState => {
  switch (action.type) {
    case Types.SET_CATEGORY:
      return {
        ...state,
        category: {
          ...state.category,
          status: action.status || null,
          data: action.data ?? [],
        },
      };
    case Types.SET_AGENTS:
      return {
        ...state,
        agents: {
          ...state.agents,
          status: action.status || null,
          data: action.data ?? [],
        },
      };
    case Types.SET_FILTERS_SELECTION:
      return {
        ...state,
        filtersSelection: action.data,
      };
    case Types.SET_CUSTOM_ATTRIBUTES:
      return {
        ...state,
        customAttributes: {
          ...state.customAttributes,
          status: action.status || null,
          data: action.data ?? [],
        },
      };
    case Types.SET_CITY:
      return {
        ...state,
        city: {
          ...state.city,
          status: action.status || null,
          data: action.data,
        },
      };
    case Types.SET_MUNICIPALITY:
      return {
        ...state,
        municipality: {
          ...state.municipality,
          status: action.status || null,
          data: action.data,
        },
      };
    case Types.RESET_STORE:
      return initialState;
    case Types.SET_SEGMENTS:
      return {
        ...state,
        segments: {
          ...state.segments,
          status: action.status || null,
          data: action.data,
        },
      };
    case Types.SET_CLIENT_CUSTOMERS:
      return {
        ...state,
        clientCustomers: {
          ...state.clientCustomers,
          status: action.status || null,
          data: action.data,
        },
      };
    case Types.GET_LIST_ZONES:
      return {
        ...state,
        zoneList: {
          ...state.zoneList,
          status: action.status || null,
          data:
            action.data === KEEP_THE_SAME ? state.zoneList.data : action.data,
        },
      };
    default:
      return {
        ...state,
      };
  }
};

export default ProductFilterReducer;
